/**
 * Beat the Yeti, per player — the pure arithmetic behind the "Beat the Yeti
 * rate" metric (hooks/useBeatYetiRate), kept out of the hook so it can be
 * reasoned about and tested on its own.
 *
 *   % beating the Yeti = (players beating the Yeti ÷ players with a score) × 100
 */
import { countsTowardAverage } from "./scoreAggregate";

/**
 * Does this player have an actual score in the window?
 *
 * The score feed writes a placeholder row (one day, every amount 0) for people
 * who have not scored anything yet, so "has a row" is not the same as "is
 * playing". Counting those rows put players who had done nothing all month into
 * the divisor and made a team of 10 read as a team of 14. A player has scored
 * when they hold points (either sign — a rep under an anchor benchmark loses
 * points, and is very much playing) or any metric with a real amount, which is
 * the same test the team averages use.
 */
export const hasActualScore = (player) => {
  if (!player) return false;
  if ((Number(player.pts) || 0) !== 0) return true;
  return Object.values(player.kpis || {}).some((cell) => countsTowardAverage(cell));
};

/**
 * Is this player ahead of the Yeti?
 *
 * Points, not amounts: a player's climb is their whole points total (every KPI
 * they score on), which is exactly what `useScopedMetrics`' `people[].pts` and
 * the Yeti's `period.total` both are — comparing per-metric amounts here would
 * answer a different question than the mountain does.
 *
 * `pts > 0` guards the start of a window, where the Yeti has not paced anything
 * yet: a rep on nothing at all has not "beaten" a Yeti on nothing at all. Same
 * guard the leaderboard's own per-metric gate uses.
 */
export const beatsYetiTotal = (playerPts, yetiPts) => {
  const p = Number(playerPts) || 0;
  if (p <= 0) return false;
  return p >= (Number(yetiPts) || 0);
};

const tally = (players, yetiByWs, bucketKey) => {
  let beating = 0;
  players.forEach((p) => {
    const yetiPts = yetiByWs?.[String(p?.wsId)]?.[bucketKey]?.total;
    if (beatsYetiTotal(p?.pts, yetiPts)) beating += 1;
  });
  return {
    beating,
    players: players.length,
    rate: players.length ? beating / players.length : 0,
  };
};

/**
 * The rate, over what `useScopedMetrics` returns.
 *
 * Each player is measured against their OWN workspace's Yeti (`yetiByWs`),
 * never the scope-wide combined pace: on a Field + In-House card that combined
 * figure is an average of two paces and belongs to neither team.
 *
 * Only players with an actual score are counted (see hasActualScore), on both
 * sides of the fraction.
 *
 * @param {Array}  people     scope roster: [{ userId, wsId, pts, kpis }]
 * @param {object} yetiByWs   { [wsId]: yetiScores } — per-workspace Yeti pace
 * @param {string} bucketKey  which Yeti period to read (yetiBucketForWindow)
 * @returns {{ beating: number, players: number, rate: number }} rate is 0–1
 */
export const beatYetiRateOf = (people, yetiByWs, bucketKey) =>
  tally((people || []).filter(hasActualScore), yetiByWs, bucketKey);

/**
 * The same count, one workspace at a time — "7 of Technicians' 10 are ahead".
 * Every id in `wsIds` gets an entry, even a team nobody has scored on yet
 * (0 of 0), so a card can say so instead of dropping the row.
 *
 * @param {string[]} wsIds
 * @returns {{ [wsId]: { beating: number, players: number, rate: number } }}
 */
export const beatYetiByWorkspace = (people, yetiByWs, bucketKey, wsIds) => {
  const scored = (people || []).filter(hasActualScore);
  const out = {};
  (wsIds || []).map(String).forEach((id) => {
    out[id] = tally(scored.filter((p) => String(p?.wsId) === id), yetiByWs, bucketKey);
  });
  return out;
};

/** How many teams one card can break the count out for. */
export const MAX_YETI_TEAMS = 6;

/**
 * The teams a Build Your Own card was asked to show under Beat the Yeti rate
 * ("Technicians 7/10"): workspace ids, in the order picked, each once, capped.
 * The one definition the editor, the card, and both save paths share, so a
 * value the card accepts is a value that survives a save.
 */
export const normalizeYetiTeams = (value) => {
  if (!Array.isArray(value)) return [];
  const seen = new Set();
  const out = [];
  value.forEach((v) => {
    const id = typeof v === "string" ? v.trim() : "";
    if (!id || seen.has(id) || out.length >= MAX_YETI_TEAMS) return;
    seen.add(id);
    out.push(id);
  });
  return out;
};

/**
 * Whether a card breaks the count out by team. Rows follow the workspaces the
 * card tracks, so this is a plain on/off. A card saved before that (with a
 * hand-picked `yetiTeams` list) counts as on.
 */
export const yetiByTeamOn = (s) =>
  !!s && (s.yetiByTeam === true || normalizeYetiTeams(s.yetiTeams).length > 0);

/** Goal notch position: a whole percent 1–100, or null (notch off). */
export const normalizeYetiGoal = (value) => {
  if (value === "" || value == null || typeof value === "boolean") return null;
  const n = Math.round(Number(value));
  return Number.isFinite(n) && n >= 1 && n <= 100 ? n : null;
};

/** Most per-workspace goals one card can carry. */
export const MAX_YETI_GOALS = 24;

/**
 * Per-workspace goals: { [workspaceId]: whole percent 1–100 }. Different teams
 * aim at different shares (Technicians is not Sales), so each tracked workspace
 * can carry its own; one with no entry uses the card's overall goal.
 */
export const normalizeYetiGoals = (value) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  const out = {};
  Object.entries(value).forEach(([id, v]) => {
    const key = String(id).trim();
    const goal = normalizeYetiGoal(v);
    if (!key || !goal || Object.keys(out).length >= MAX_YETI_GOALS) return;
    out[key] = goal;
  });
  return out;
};

/**
 * The goal a workspace's bar is marked with: its own if it has one, else the
 * card's overall goal. No overall goal means the notch is switched off, and a
 * team's own goal does not turn it back on.
 */
export const yetiGoalFor = (goals, overall, workspaceId) => {
  const base = normalizeYetiGoal(overall);
  if (!base) return null;
  return normalizeYetiGoal(goals && goals[String(workspaceId)]) || base;
};

/** The goal a new notch starts at when the admin switches it on. */
export const DEFAULT_YETI_GOAL = 50;

/** Has this rate (0–1) reached the goal (whole percent)? Compared on whole
    percents, the way the chip beside it prints it. */
export const reachedYetiGoal = (rate, goalPct) =>
  Math.round((Number(rate) || 0) * 100) >= (Number(goalPct) || 0);
